import { AlertTriangle } from "lucide-react";
import { CopyButton } from "@/components/copy/CopyButton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";

interface OneTimeSecretDisplayProps {
  /**
   * The secret value to display (e.g., API key, webhook secret)
   */
  secret: string;
  /**
   * Label to show above the secret input
   */
  label?: string;
  /**
   * Custom alert message to show above the secret
   */
  alertMessage?: string;
  /**
   * Custom help text to show below the secret
   */
  helpText?: string;
  /**
   * Whether to show the footer with Done button
   */
  showFooter?: boolean;
  /**
   * Callback when Done is clicked
   */
  onDone?: () => void;
}

export function OneTimeSecretDisplay({
  secret,
  label = "Secret",
  alertMessage = "This secret will only be shown once. Please store it securely.",
  helpText = "Store this secret in a secure location. You won't be able to see it again.",
  showFooter = true,
  onDone,
}: OneTimeSecretDisplayProps) {
  return (
    <div className="space-y-4">
      <Alert
        variant="default"
        className="text-yellow-600 border-yellow-500/50 bg-yellow-500/10 dark:text-yellow-400"
      >
        <AlertTriangle className="w-4 h-4" />
        <AlertDescription className="text-sm font-medium">
          {alertMessage}
        </AlertDescription>
      </Alert>

      <div className="space-y-2">
        <FormLabel>{label}</FormLabel>
        <div className="flex space-x-2">
          <Input readOnly value={secret} type="text" className="font-mono" />
          <CopyButton text={secret} />
        </div>
        <p className="text-sm text-muted-foreground">{helpText}</p>
      </div>

      {showFooter && (
        <div className="flex justify-end">
          <Button type="button" variant="outline" onClick={onDone}>
            Done
          </Button>
        </div>
      )}
    </div>
  );
}
